<template>
    <div class="modal-card terceros-card">
        <header class="modal-card-head has-background-white">
            <h3 class="modal-card-title has-text-centered">
                Gestión de terceros
            </h3>
            <button class="delete" aria-label="Cerrar Modal" @click="$emit('close')"></button>
        </header>
        <section class="modal-card-body">
            <div class="terceros-toolbar">
                <button class="button is-small" :disabled="loading || reconciling"
                    @click="reconcile">
                    {{ reconciling ? "Reconciliando…" : "🔄 Reconciliar ahora" }}
                </button>
                <span class="terceros-toolbar__hint">
                    Corre la misma verificación contra Fintoc del cron semanal
                    y guarda el resultado.
                </span>
            </div>

            <div v-if="!fintocAvailable" class="fintoc-down">
                Fintoc no respondió: se muestra solo el estado local. Reintentá
                en unos segundos para ver la reconciliación real.
            </div>

            <div v-if="loading" class="has-text-centered">Cargando…</div>
            <div v-else-if="!connections.length" class="has-text-centered empty-text">
                Esta empresa no tiene conexiones a Fintoc.
            </div>

            <div v-for="conn in connections" :key="conn.cpl_id" class="conn-card">
                <div class="conn-head">
                    <div>
                        <div class="conn-name">
                            <template v-if="conn.kind === 'SII'">🧾 SII</template>
                            <template v-else>🏦 {{ conn.bank_name || "Banco (sin identificar)" }}</template>
                        </div>
                        <div v-if="conn.db.used_at" class="conn-meta">
                            Última sincronización: {{ formatDate(conn.db.used_at) }}
                        </div>
                    </div>
                    <div class="conn-chips">
                        <span class="chip" :class="appChip(conn).cls" :title="conn.db.detail">
                            <span class="chip__dot"></span> App: {{ appChip(conn).label }}
                        </span>
                        <span class="chip" :class="fintocChip(conn).cls">
                            <span class="chip__dot"></span> Fintoc: {{ fintocChip(conn).label }}
                        </span>
                    </div>
                </div>

                <div v-if="reconciliationBadge(conn)" class="evidence-row">
                    <span class="evidence" :class="reconciliationBadge(conn).cls">
                        {{ reconciliationBadge(conn).label }}
                    </span>
                    <p v-if="conn.fintoc && conn.fintoc.found === null && conn.fintoc.reconciled_at"
                        class="conn-meta">
                        Según la reconciliación diaria del {{ formatDate(conn.fintoc.reconciled_at) }}
                    </p>
                </div>

                <ul v-if="conn.accounts.length" class="conn-accounts">
                    <li v-for="account in conn.accounts" :key="account.number">
                        {{ account.account_type || "Cuenta" }} ·
                        {{ account.number }} · {{ account.currency }}
                    </li>
                </ul>

                <div v-if="fintocFailedId === conn.cpl_id" class="fintoc-fail">
                    <p class="fintoc-fail__msg">
                        ⚠ Fintoc no respondió: la conexión sigue activa allá.
                        Podés reintentar más tarde o desconectar solo en la app
                        (el desfase quedará marcado acá para cerrarlo después).
                    </p>
                    <div class="conn-actions">
                        <button class="button is-small" :disabled="busyId === conn.cpl_id"
                            @click="fintocFailedId = null">
                            Dejar activo por ahora
                        </button>
                        <button class="button is-small is-danger" :disabled="busyId === conn.cpl_id"
                            @click="disconnectLocalOnly(conn)">
                            Desconectar solo en la app
                        </button>
                    </div>
                </div>
                <div v-else-if="isDisconnectable(conn)" class="conn-actions">
                    <template v-if="confirmingId === conn.cpl_id">
                        <span class="confirm-msg">¿Desconectar también en Fintoc?</span>
                        <button class="button is-small" :disabled="busyId === conn.cpl_id"
                            @click="confirmingId = null">
                            Mantener
                        </button>
                        <button class="button is-small is-danger" :disabled="busyId === conn.cpl_id"
                            @click="disconnect(conn)">
                            Sí, desconectar
                        </button>
                    </template>
                    <button v-else class="button is-small is-danger is-light"
                        :disabled="busyId !== null" @click="confirmingId = conn.cpl_id">
                        Desconectar
                    </button>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import moment from "moment";
import { mapActions } from "vuex";

import { notificate, consoleHandlerError } from "@/utils/message";

// Semáforo local (derived_status del backend, mismo clasificador que el panel).
const APP_CHIPS = {
    OK: { cls: "is-ok", label: "conectado" },
    CONNECTING: { cls: "is-progress", label: "conectando" },
    LIKELY_BROKEN: { cls: "is-warn", label: "a revisar" },
    BROKEN: { cls: "is-bad", label: "con problemas" },
    NOT_APPLICABLE: { cls: "is-off", label: "desconectado" },
};

// Mensajes por veredicto de reconciliación BD ↔ Fintoc (solo los desfases).
const RECONCILIATION_BADGES = {
    DB_ON_FINTOC_OFF: {
        cls: "is-warning",
        label: "⚠ Conectado acá pero inactivo en Fintoc — los datos están congelados",
    },
    DB_OFF_FINTOC_ON: {
        cls: "is-warning",
        label: "⚠ Desconectado acá pero sigue activo en Fintoc — desconectalo para cortar el cobro",
    },
    MISSING_IN_FINTOC: {
        cls: "is-warning",
        label: "⚠ El link ya no existe en Fintoc",
    },
    NO_LINK_ID: {
        cls: "is-note",
        label: "Alta sin completar — nunca llegó a crearse en Fintoc",
    },
    UNKNOWN: {
        cls: "is-note",
        label: "Fintoc no respondió — se muestra solo el estado local",
    },
};

export default {
    name: "GestionTerceros",
    props: {
        companyId: { type: Number, required: true },
    },
    data() {
        return {
            connections: [],
            fintocAvailable: true,
            loading: false,
            reconciling: false,
            busyId: null,
            confirmingId: null,
            // cpl con desconexión fallida por Fintoc: muestra la elección
            // "solo en la app" / "dejar activo por ahora".
            fintocFailedId: null,
        };
    },
    mounted() {
        this.load();
    },
    methods: {
        ...mapActions("analystCompany", [
            "fetchCompanyConnections",
            "disconnectCompanyConnection",
            "reconcileCompanyConnections",
        ]),
        async load() {
            this.loading = true;
            try {
                const data = await this.fetchCompanyConnections(this.companyId);
                this.connections = data.connections;
                this.fintocAvailable = data.fintoc_available;
            } catch (error) {
                consoleHandlerError({ error });
            } finally {
                this.loading = false;
            }
        },
        async reconcile() {
            this.reconciling = true;
            try {
                await this.reconcileCompanyConnections(this.companyId);
                notificate(
                    "Reconciliación completada.",
                    "is-success is-light", "is-top-right", false, 5000,
                );
                await this.load();
            } catch (error) {
                const is503 = error?.response?.status === 503;
                notificate(
                    is503
                        ? "Fintoc no respondió; reintentá en unos segundos."
                        : "No pudimos correr la reconciliación.",
                    "is-danger is-light", "is-top-right", false, 5000,
                );
                consoleHandlerError({ error, silent: true });
            } finally {
                this.reconciling = false;
            }
        },
        async disconnect(conn) {
            this.busyId = conn.cpl_id;
            try {
                await this.disconnectCompanyConnection({
                    companyId: this.companyId,
                    cplId: conn.cpl_id,
                });
                notificate(
                    "Conexión desconectada (también en Fintoc).",
                    "is-success is-light", "is-top-right", false, 5000,
                );
                await this.load();
            } catch (error) {
                if (error?.response?.status === 503) {
                    // Fintoc no respondió: se ofrece desconectar solo en la
                    // app o dejarlo activo por ahora (bloque inline).
                    this.fintocFailedId = conn.cpl_id;
                } else {
                    notificate(
                        "No pudimos desconectar la conexión.",
                        "is-danger is-light", "is-top-right", false, 5000,
                    );
                }
                consoleHandlerError({ error, silent: true });
            } finally {
                this.busyId = null;
                this.confirmingId = null;
            }
        },
        async disconnectLocalOnly(conn) {
            this.busyId = conn.cpl_id;
            try {
                await this.disconnectCompanyConnection({
                    companyId: this.companyId,
                    cplId: conn.cpl_id,
                    localOnly: true,
                });
                notificate(
                    "Desconectado solo en la app; sigue activo en Fintoc — " +
                        "reintentá la desconexión real más tarde.",
                    "is-warning is-light", "is-top-right", false, 5000,
                );
                await this.load();
            } catch (error) {
                notificate(
                    "No pudimos desconectar la conexión.",
                    "is-danger is-light", "is-top-right", false, 5000,
                );
                consoleHandlerError({ error, silent: true });
            } finally {
                this.busyId = null;
                this.fintocFailedId = null;
            }
        },
        appChip(conn) {
            return APP_CHIPS[conn.db.derived_status] || APP_CHIPS.NOT_APPLICABLE;
        },
        fintocChip(conn) {
            const fintoc = conn.fintoc || {};
            if (fintoc.found === null || fintoc.found === undefined) {
                // Fintoc caído: si hay estado persistido por la última
                // reconciliación, se muestra ese (la nota lo fecha).
                if (fintoc.status === "login_required") {
                    return { cls: "is-warn", label: "clave requerida" };
                }
                if (fintoc.status === "active") {
                    return { cls: "is-ok", label: "activo" };
                }
                if (fintoc.status === "inactive") {
                    return { cls: "is-off", label: "inactivo" };
                }
                return { cls: "is-off", label: "sin datos" };
            }
            if (!fintoc.found) {
                return { cls: "is-bad", label: "no existe" };
            }
            if (fintoc.status === "login_required") {
                return { cls: "is-warn", label: "clave requerida" };
            }
            return fintoc.active
                ? { cls: "is-ok", label: "activo" }
                : { cls: "is-off", label: "inactivo" };
        },
        reconciliationBadge(conn) {
            return RECONCILIATION_BADGES[conn.reconciliation] || null;
        },
        isDisconnectable(conn) {
            // Conectado en la app, o desfasado con Fintoc todavía activo
            // (desconectar re-ejecuta la desactivación y cierra el desfase).
            const dbOn = conn.db.is_active && !conn.db.user_desactive_this;
            return dbOn || conn.fintoc?.active === true;
        },
        formatDate(value) {
            return moment(value).format("DD-MM-YYYY HH:mm");
        },
    },
};
</script>

<style lang="scss" scoped>
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss).
.terceros-card {
    font-family: var(--ap-font);
    color: var(--ap-ink-2);
    width: clamp(320px, 90vw, 640px);
}

:deep(.modal-card-title) {
    font-family: var(--ap-font);
    font-weight: 700;
    font-size: 20px;
    letter-spacing: -0.01em;
    color: var(--ap-ink);
}

.empty-text {
    color: var(--ap-muted);
    padding: 32px 0;
}

.terceros-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.terceros-toolbar__hint {
    font-size: 12px;
    color: var(--ap-slate);
}

.fintoc-down {
    margin-bottom: 12px;
    padding: 8px 12px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-sm);
    background-color: var(--ap-mist);
    color: var(--ap-slate);
    font-size: 12.5px;
}

.conn-card {
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius-sm);
    padding: 16px;
    margin-bottom: 12px;
    background-color: var(--ap-paper);
    box-shadow: var(--ap-shadow-card);
}

.conn-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
}

.conn-name {
    font-weight: 600;
    color: var(--ap-ink);
}

.conn-meta {
    font-size: 12px;
    color: var(--ap-slate);
    margin-top: 2px;
    font-variant-numeric: tabular-nums;
}

.conn-chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

// Chips de estado — mismo lenguaje visual que el semáforo del panel.
.chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    padding: 3px 10px;
    border-radius: var(--ap-radius-pill);
    border: 1px solid transparent;
    white-space: nowrap;
}

.chip__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: currentColor;
}

.chip.is-ok { color: var(--ap-ok); background-color: var(--ap-ok-wash); }
.chip.is-progress { color: var(--ap-teal-deep); background-color: var(--ap-teal-wash); }
.chip.is-warn { color: var(--ap-warn); background-color: var(--ap-warn-wash); }
.chip.is-bad { color: var(--ap-alert); background-color: var(--ap-alert-wash); }
.chip.is-off { color: var(--ap-slate); background-color: var(--ap-mist); }

.evidence-row {
    margin: 10px 0 0;
}

.evidence {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    padding: 3px 10px;
    border-radius: var(--ap-radius-pill);
    border: 1px solid transparent;
}

.evidence.is-warning {
    color: var(--ap-alert);
    background-color: var(--ap-alert-wash);
    border-color: rgba(192, 69, 59, 0.28);
}

.evidence.is-note {
    color: var(--ap-slate);
    background-color: var(--ap-mist);
    border-color: var(--ap-line);
}

.conn-accounts {
    margin: 10px 0 0;
    padding-left: 18px;
    font-size: 12.5px;
    color: var(--ap-slate);
    font-variant-numeric: tabular-nums;

    li {
        list-style: disc;
    }
}

.conn-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
}

.confirm-msg {
    font-size: 12.5px;
    color: var(--ap-alert);
    font-weight: 600;
}

.fintoc-fail {
    margin-top: 12px;
    padding: 10px 12px;
    border: 1px solid rgba(192, 69, 59, 0.28);
    border-radius: var(--ap-radius-sm);
    background-color: var(--ap-alert-wash);
}

.fintoc-fail__msg {
    font-size: 12.5px;
    color: var(--ap-alert);
    margin-bottom: 8px;
}
</style>
